import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	EventEmitter,
	HostListener,
	HostBinding,
	Component,
	OnInit,
	Output,
	Input,
} from '@angular/core';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { SupportModalService } from '@app/support-request/support-modal-component/support-modal.service';
import { ISidebarRestrictions } from '@app/sidebar-layout/interfaces/i-sidebar-restrictions';
import { SuccessSupportModalComponent, SupportModalComponent } from '@app/support-request';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { IOrganization } from '@app/core/services/organization/organization.interface';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { SectionChapterEnum } from '@app/sidebar-layout/enums/section-chapter.enum';
import { PresenterModeService } from '@app/services/presenter-mode.service';
import { OrganizationsService } from '@app/services/organizations.service';
import { ISection } from '@app/sidebar-layout/interfaces/i-section';
import { UnsubscriberBase } from '@core/services/unsubscriber.base';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { UserService } from '@app/services/user/user.service';
import { MatDialog } from '@angular/material/dialog';
import { take, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ServiceLinkIcons } from '@app/sidebar-layout/config/sidebarIcons';
import { ConfigFacadeService } from '@app/core/services/config/config-facade.service';
import { PartnerAliasEnum } from '@partners/partner-alias.enum';
import { AnalyticsEvents, AnalyticStatus, MixpanelService } from '@app/services/mixpanel.service';
import { animate, state, style, transition, trigger } from '@angular/animations';

@Component({
	selector: 'app-sidebar',
	templateUrl: './sidebar.component.html',
	styleUrls: ['./sidebar.component.scss'],
	animations: [
		trigger('rotateArrow', [
			state('true', style({ transform: 'rotate(180deg)' })),
			state('false', style({ transform: 'rotate(0)' })),
			transition('true <=> false', animate('200ms ease')),
		]),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent extends UnsubscriberBase implements OnInit {
	@Output() public onSizeChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
	@HostBinding('class.collapsed') private _isCollapsed: boolean;

	@FeatureFlag(FEATURE_FLAGS.ENABLE_CONTAINERS_POD_RECOMMENDATION) enableContainersPodRecommendation: boolean;
	@FeatureFlag(FEATURE_FLAGS.ENABLE_APPROVE_BILLING) enableApproveBilling: boolean;
	@FeatureFlag(FEATURE_FLAGS.ENABLE_COMMUNITY_PORTAL) enableCommunityPortal: boolean;
	@FeatureFlag(FEATURE_FLAGS.ENABLE_NEW_ONBOARDING) enableNewOnboarding: boolean;
	//@todo remove with ZES-11735
	@Input() enableOptimizationHub: boolean;

	@Input() set isCollapsed(isCollapsed: boolean) {
		this._isCompactView = isCollapsed;
		this._isCollapsed = isCollapsed;
	}

	@Input() set sections(sections: ISection[]) {
		this._sections = sections;
	}

	@Input() set section(section: ISection) {
		this._currentSection = section;
		if (this._sections) {
			this._sections.forEach((section) => (section.isExpanded = false));
		}
		if (this._currentSection) {
			this._currentSection.isExpanded = true;
			this.cdr.detectChanges();
		}
	}

	@Input()
	public set restrictions(restrictions: ISidebarRestrictions) {
		this._restrictions = restrictions;
	}

	@HostListener('mouseleave')
	private onMouseLeave(): void {
		if (this._isCompactView) {
			this._isCollapsed = true;
		}
	}

	public PARTNER_PORTAL: 'Partner Portal' = 'Partner Portal';
	public isPartnerSupportForm: boolean;
	public supportIcon = ServiceLinkIcons.supportIcon;
	public readonly sectionChapterEnum = SectionChapterEnum;
	public readonly appRouteHelper = AppRouteHelper;
	public isSupportAndDocsBtnsVisible: boolean;
	public currentUserOrganization: IOrganization;

	private _restrictions: ISidebarRestrictions = null;
	private _currentSection: ISection;
	private _isCompactView: boolean;
	private _sections: ISection[];

	constructor(
		public readonly presenterMode: PresenterModeService,
		private readonly organizationsService: OrganizationsService,
		private readonly supportModalService: SupportModalService,
		private readonly userService: UserService,
		private readonly sanitizer: DomSanitizer,
		private readonly cdr: ChangeDetectorRef,
		private readonly dialog: MatDialog,
		private readonly router: Router,
		private configFacadeService: ConfigFacadeService,
		private mixpanelService: MixpanelService,
		public featureFlagManagerService: FeatureFlagManagerService,
	) {
		super();
	}

	public ngOnInit(): void {
		this.supportModalService.isSupportAvailable$
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((isAvailable) => (this.isSupportAndDocsBtnsVisible = isAvailable));
		this.organizationsService
			.getCurrentUserOrganization()
			.pipe(take(1))
			.subscribe((organization: IOrganization) => {
				this.currentUserOrganization = organization;
				this.cdr.detectChanges();
				this.isPartnerSupportForm = this.isValidPartnerToSupportForm();
			});
		this.supportModalService.showRequest$.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
			this.openSupportModal();
		});
		this.cdr.markForCheck();
	}

	public openSupportModal(): void {
		if (this.dialog.openDialogs.length == 0) {
			const dialogRef = this.dialog.open(SupportModalComponent, {
				width: '557px',
				height: '645px',
				data: {
					userName: this.userService.userNickname,
					userEmail: this.userService.userEmail,
					orgName: this.isPartnerSupportForm
						? `(${this.configFacadeService.partnerName})`
						: this.currentUserOrganization.org_name,
				},
			});
			dialogRef
				.afterClosed()
				.pipe(take(1))
				.subscribe((isSubmitted) => {
					if (isSubmitted) {
						this.dialog.open(SuccessSupportModalComponent, {
							width: '431px',
							height: '370px',
							data: {
								userEmail: this.userService.userEmail,
							},
						});
					}
				});
		}
	}

	public pickSection(section: ISection): void {
		this.router.navigateByUrl(SidebarComponent.findDefaultPageForSection(section)).then();
	}

	public onAddAccountClicked(): void {
		const path = this.enableNewOnboarding ? AppRouteHelper.onboardAccount : AppRouteHelper.onboarding;
		void this.router.navigate([`/${path}`]);
	}

	public onBurgerHover(): void {
		this._isCollapsed = false;
	}

	public logout(): void {
		this.userService.logout();
		this.mixpanelService.track(AnalyticsEvents.sessionOver, { Status: AnalyticStatus.timeOut });
	}

	public toggle(): void {
		if (this._isCompactView && !this._isCollapsed) {
			this.onSizeChanged.next(this._isCollapsed);
		} else {
			this.onSizeChanged.next(!this._isCollapsed);
		}
	}

	public get isAddAccountAllowed(): boolean {
		return this._restrictions?.isAddAccountAllowed;
	}

	public get isAuditLogAllowed(): boolean {
		return this._restrictions?.isAuditLogAllowed;
	}

	public get currentSection(): ISection {
		return this._currentSection;
	}

	public get isCollapsed(): boolean {
		return this._isCollapsed;
	}

	public get sections(): ISection[] {
		return this._sections;
	}

	private static findDefaultPageForSection(section: ISection): string {
		const chapter = section.chapters[0];
		if (chapter.type === SectionChapterEnum.Link) {
			return chapter.url;
		}
		return chapter.pages[0].url;
	}

	public sanitizeSvg(svg: string): SafeHtml {
		return this.sanitizer.bypassSecurityTrustHtml(svg);
	}

	public openDocumentation(): void {
		const docURL = 'https://docs.zesty.co/';
		window.open(docURL, '_blank');
	}

	public openCommunityPortal(): void {
		const docURL = 'https://community.zesty.co/';
		window.open(docURL, '_blank');
	}

	private isValidPartnerToSupportForm(): boolean {
		return !!this.configFacadeService.partnerId && this.configFacadeService.partnerPrefix !== PartnerAliasEnum.Zesty;
	}
}
